import { Answer, ClientInfo } from '../types';

export class UrlEncoderService {
  /**
   * Encode answers and client info into a compact URL format
   * We need to pass questions to get the option codes
   */
  static encodeResults(
    answers: Map<string, Answer>, 
    clientInfo: ClientInfo,
    questions: any[]
  ): string {
    // Create an array of answer codes
    const answerCodes: string[] = [];
    
    // Map answer values to their codes
    answers.forEach((answer) => {
      const question = questions.find(q => q.id === answer.questionId);
      if (question) {
        answer.values.forEach(value => {
          // Find the option that matches this value
          const option = question.options.find((opt: any) => 
            opt.label === value || opt.value === value
          );
          if (option && option.code) {
            answerCodes.push(option.code);
          }
        });
      }
    });
    
    // Create a compact object with minimal data
    const data = {
      a: answerCodes, // answer codes
      c: {  // client info (minimal)
        p: clientInfo.prenom,
        n: clientInfo.nom,
        e: clientInfo.email,
        t: clientInfo.telephone,
        m: clientInfo.acceptMarketing ? 1 : 0
      }
    };
    
    // Encode to base64 URL-safe string
    const jsonString = JSON.stringify(data);
    const base64 = btoa(encodeURIComponent(jsonString).replace(/%([0-9A-F]{2})/g, (_match, p1) => {
      return String.fromCharCode(parseInt(p1, 16));
    }));
    
    // Make URL-safe
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }
  
  /**
   * Decode URL parameter back to answers and client info
   */
  static decodeResults(encoded: string): { answers: string[], clientInfo: Partial<ClientInfo> } | null {
    try {
      // Restore base64 padding and convert URL-safe chars back
      const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
      const padding = base64.length % 4;
      const paddedBase64 = base64 + (padding ? '===='.substring(padding) : '');
      
      // Decode from base64
      const jsonString = decodeURIComponent(atob(paddedBase64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const data = JSON.parse(jsonString);
      
      return {
        answers: data.a || [],
        clientInfo: {
          prenom: data.c?.p || '',
          nom: data.c?.n || '',
          email: data.c?.e || '',
          telephone: data.c?.t || '',
          acceptMarketing: data.c?.m === 1,
          acceptTerms: true
        }
      };
    } catch (error) {
      console.error('Error decoding URL:', error);
      return null;
    }
  }
  
  /**
   * Generate a shareable URL with encoded data
   */
  static generateShareableUrl(
    answers: Map<string, Answer>, 
    clientInfo: ClientInfo,
    questions: any[]
  ): string {
    const encoded = this.encodeResults(answers, clientInfo, questions);
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?r=${encoded}`;
  }
}
